import {
  LOG_FIELDS
} from "./chunk-KKQPUID3.js";

// src/browser/is-abort-error.ts
function isAbortError(value) {
  if (!value || typeof value !== "object") return false;
  const candidate = value;
  if (candidate.name === "CanceledError" || candidate.name === "AbortError") {
    return true;
  }
  if (candidate.code === "ERR_CANCELED") return true;
  if (candidate.name === "AxiosError" && (candidate.code === "ECONNABORTED" || candidate.message === "Request aborted")) {
    return true;
  }
  return false;
}

// src/browser/log.ts
import {
  logs,
  SeverityNumber
} from "@opentelemetry/api-logs";
var loggerName = "observability-uninitialized";
function setLoggerName(name) {
  loggerName = name;
}
function emit(severityNumber, severityText, body, attributes) {
  try {
    logs.getLogger(loggerName).emit({ severityNumber, severityText, body, attributes });
  } catch {
  }
}
var log = {
  debug: (body, attributes) => emit(SeverityNumber.DEBUG, "DEBUG", body, attributes),
  info: (body, attributes) => emit(SeverityNumber.INFO, "INFO", body, attributes),
  warn: (body, attributes) => emit(SeverityNumber.WARN, "WARN", body, attributes),
  error: (body, attributes) => emit(SeverityNumber.ERROR, "ERROR", body, attributes)
};

// src/browser/otel-init.ts
import "zone.js";
import { logs as logs2 } from "@opentelemetry/api-logs";
import { getWebAutoInstrumentations } from "@opentelemetry/auto-instrumentations-web";
import { ZoneContextManager } from "@opentelemetry/context-zone-peer-dep";
import { OTLPLogExporter } from "@opentelemetry/exporter-logs-otlp-http";
import { OTLPTraceExporter } from "@opentelemetry/exporter-trace-otlp-http";
import { registerInstrumentations } from "@opentelemetry/instrumentation";
import { resourceFromAttributes } from "@opentelemetry/resources";
import {
  BatchLogRecordProcessor,
  LoggerProvider
} from "@opentelemetry/sdk-logs";
import {
  BatchSpanProcessor,
  WebTracerProvider
} from "@opentelemetry/sdk-trace-web";
import {
  ATTR_DEPLOYMENT_ENVIRONMENT_NAME,
  ATTR_SERVICE_NAME,
  ATTR_SERVICE_VERSION
} from "@opentelemetry/semantic-conventions/incubating";

// src/browser/otel-auto-capture.ts
var installed = false;
var SHIP_MAX_PER_WINDOW = 5;
var SHIP_WINDOW_MS = 6e4;
function createRateLimiter(maxPerWindow, windowMs) {
  const state = /* @__PURE__ */ new Map();
  return function allow(key, now) {
    const entry = state.get(key);
    if (!entry || now >= entry.resetAt) {
      state.set(key, { count: 1, resetAt: now + windowMs });
      if (state.size > 256) {
        for (const [existingKey, existing] of state) {
          if (now >= existing.resetAt) state.delete(existingKey);
        }
      }
      return true;
    }
    if (entry.count >= maxPerWindow) return false;
    entry.count += 1;
    return true;
  };
}
function setupAutoCapture() {
  if (installed) return;
  installed = true;
  const allow = createRateLimiter(SHIP_MAX_PER_WINDOW, SHIP_WINDOW_MS);
  window.addEventListener("error", (event) => {
    if (isAbortError(event.error)) return;
    if (!allow(`window.onerror:${event.message}`, Date.now())) return;
    log.error("window.onerror", {
      [LOG_FIELDS.EXCEPTION_MESSAGE]: event.message,
      [LOG_FIELDS.EXCEPTION_STACK]: event.error?.stack,
      [LOG_FIELDS.URL_PATH]: window.location.pathname
    });
  });
  window.addEventListener("unhandledrejection", (event) => {
    if (isAbortError(event.reason)) {
      event.preventDefault();
      return;
    }
    const reason = event.reason;
    const message = typeof reason === "string" ? reason : reason?.message ?? String(reason);
    if (!allow(`unhandled_rejection:${message}`, Date.now())) return;
    log.error("unhandled_rejection", {
      [LOG_FIELDS.EXCEPTION_MESSAGE]: message,
      [LOG_FIELDS.EXCEPTION_STACK]: typeof reason === "string" ? void 0 : reason?.stack,
      [LOG_FIELDS.URL_PATH]: window.location.pathname
    });
  });
  wrapConsole("error", "console.error", allow);
  wrapConsole("warn", "console.warn", allow);
}
function wrapConsole(level, body, allow) {
  const original = console[level].bind(console);
  console[level] = makeConsoleSink(
    level,
    body,
    original,
    allow,
    Date.now,
    () => window.location.pathname
  );
}
function makeConsoleSink(level, body, original, allow, now, pathname) {
  return (...args) => {
    if (args.some(isAbortError)) return;
    if (allow(`${body}:${messageKey(args)}`, now())) {
      try {
        const firstError = args.find(
          (arg) => arg instanceof Error
        );
        log[level](body, {
          "console.args": args.map(
            (arg) => arg instanceof Error ? `${arg.name}: ${arg.message}` : safeToString(arg)
          ),
          [LOG_FIELDS.EXCEPTION_STACK]: firstError?.stack,
          [LOG_FIELDS.URL_PATH]: pathname()
        });
      } catch {
      }
    }
    original(...args);
  };
}
function messageKey(args) {
  const first = args[0];
  if (first instanceof Error) return `${first.name}: ${first.message}`;
  if (typeof first === "string") return first;
  return safeToString(first);
}
function safeToString(value) {
  if (typeof value === "string") return value;
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
}

// src/browser/otel-init.ts
var initialized = false;
var TELEMETRY_URLS = [
  /\/v1\/otel\//,
  /\/sentry\b/,
  /\/v1\/web-vitals\b/,
  /\/v1\/events\b/
];
function initOtelBrowser(config) {
  if (initialized) return;
  if (config.disabled) {
    initialized = true;
    return;
  }
  initialized = true;
  setLoggerName(config.serviceName);
  const tracesUrl = config.endpoints?.traces ?? "/v1/otel/traces";
  const logsUrl = config.endpoints?.logs ?? "/v1/otel/logs";
  const propagateTraceHeaderCorsUrls = config.propagateTraceHeaderUrls ?? [
    new RegExp(`^${escapeRegExp(window.location.origin)}`)
  ];
  const resource = resourceFromAttributes({
    [ATTR_SERVICE_NAME]: config.serviceName,
    [ATTR_SERVICE_VERSION]: config.appVersion,
    [ATTR_DEPLOYMENT_ENVIRONMENT_NAME]: config.appEnv
  });
  const tracerProvider = new WebTracerProvider({
    resource,
    spanProcessors: [
      new BatchSpanProcessor(new OTLPTraceExporter({ url: tracesUrl }))
    ]
  });
  tracerProvider.register({ contextManager: new ZoneContextManager() });
  const loggerProvider = new LoggerProvider({
    resource,
    processors: [
      new BatchLogRecordProcessor({
        exporter: new OTLPLogExporter({ url: logsUrl })
      })
    ]
  });
  logs2.setGlobalLoggerProvider(loggerProvider);
  registerInstrumentations({
    tracerProvider,
    instrumentations: getWebAutoInstrumentations({
      "@opentelemetry/instrumentation-document-load": {},
      "@opentelemetry/instrumentation-user-interaction": {},
      "@opentelemetry/instrumentation-xml-http-request": {
        propagateTraceHeaderCorsUrls,
        ignoreUrls: TELEMETRY_URLS
      },
      "@opentelemetry/instrumentation-fetch": {
        /** off: it calls Response.clone() on an already-consumed body and
         *  throws. the api client is axios (xhr), so xhr instrumentation
         *  already carries propagation. */
        enabled: false
      }
    })
  });
  setupAutoCapture();
}
function escapeRegExp(value) {
  return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

// src/browser/events.ts
import {
  ANALYTICS_BATCH_MAX_EVENTS,
  ANALYTICS_PROPERTIES_MAX_CHARS
} from "@tandemone/types";
var ANON_KEY = "tandemone:aid";
var STITCH_KEY = "tandemone:aid-stitched";
var FLUSH_AT = 10;
var FLUSH_INTERVAL_MS = 5e3;
var endpoint = "/v1/events";
var disabled = false;
var queue = [];
var timer = null;
var listenersBound = false;
function uuid() {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (ch) => {
    const nibble = Math.random() * 16 | 0;
    return (ch === "x" ? nibble : nibble & 3 | 8).toString(16);
  });
}
function readStore(key) {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}
function writeStore(key, value) {
  try {
    localStorage.setItem(key, value);
  } catch {
  }
}
var anonymousId = readStore(ANON_KEY) ?? uuid();
writeStore(ANON_KEY, anonymousId);
function getAnonymousId() {
  return anonymousId;
}
function initEvents(config = {}) {
  endpoint = config.endpoint ?? "/v1/events";
  disabled = config.disabled ?? false;
  if (disabled || listenersBound || typeof window === "undefined") return;
  listenersBound = true;
  window.addEventListener("pagehide", () => flush(true));
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") flush(true);
  });
}
function scheduleFlush() {
  if (timer) return;
  timer = setTimeout(() => {
    timer = null;
    flush(false);
  }, FLUSH_INTERVAL_MS);
}
function trimProperties(properties) {
  if (!properties) return void 0;
  try {
    if (JSON.stringify(properties).length <= ANALYTICS_PROPERTIES_MAX_CHARS) {
      return properties;
    }
  } catch {
    return void 0;
  }
  return { _truncated: true };
}
function emit2(event, properties, meta = {}) {
  if (disabled) return;
  const props = properties;
  queue.push({
    eventId: uuid(),
    name: event,
    occurredAt: (/* @__PURE__ */ new Date()).toISOString(),
    path: meta.path ?? window.location.pathname,
    ...meta.durationMs === void 0 ? {} : { durationMs: meta.durationMs },
    properties: trimProperties(props)
  });
  if (queue.length >= FLUSH_AT) flush(false);
  else scheduleFlush();
}
function send(url, body) {
  return fetch(url, {
    method: "POST",
    body,
    headers: { "Content-Type": "application/json" },
    credentials: "same-origin",
    keepalive: true
  });
}
function post(body, unloading) {
  try {
    if (unloading && typeof navigator.sendBeacon === "function") {
      navigator.sendBeacon(
        endpoint,
        new Blob([body], { type: "application/json" })
      );
      return;
    }
    void send(endpoint, body).then((response) => {
      if (!response.ok && response.status >= 500) retryOnce(body);
    }).catch(() => retryOnce(body));
  } catch {
  }
}
function retryOnce(body) {
  try {
    void send(endpoint, body).catch(() => {
    });
  } catch {
  }
}
function flush(unloading = false) {
  if (disabled || queue.length === 0) return;
  if (timer) {
    clearTimeout(timer);
    timer = null;
  }
  const pending = queue;
  queue = [];
  for (let i = 0; i < pending.length; i += ANALYTICS_BATCH_MAX_EVENTS) {
    const events = pending.slice(i, i + ANALYTICS_BATCH_MAX_EVENTS);
    post(JSON.stringify({ anonymousId, events }), unloading);
  }
}
function identifyUser(userId) {
  if (disabled) return;
  if (readStore(STITCH_KEY) === `${userId}:${anonymousId}`) return;
  flush(false);
  try {
    void send(`${endpoint}/identify`, JSON.stringify({ anonymousId })).then((response) => {
      if (response.ok) writeStore(STITCH_KEY, `${userId}:${anonymousId}`);
    }).catch(() => {
    });
  } catch {
  }
}
function resetEvents() {
  flush(false);
  anonymousId = uuid();
  writeStore(ANON_KEY, anonymousId);
  try {
    localStorage.removeItem(STITCH_KEY);
  } catch {
  }
}

// src/browser/analytics.ts
function initAnalytics(config = {}) {
  initEvents({ disabled: config.disabled });
}
function track(event, properties, meta = {}) {
  emit2(event, properties, meta);
}
function identify(userId) {
  identifyUser(userId);
}
function reset() {
  resetEvents();
}

// src/browser/sentry.ts
import * as Sentry from "@sentry/react";
var initialized2 = false;
function initSentryBrowser(config) {
  if (initialized2) return;
  if (!config.dsn || config.disabled) {
    initialized2 = true;
    return;
  }
  Sentry.init({
    dsn: config.dsn,
    environment: config.appEnv,
    release: config.appVersion,
    sendDefaultPii: true,
    tunnel: "/sentry",
    integrations: (integrations) => integrations.filter((i) => i.name !== "BrowserSession"),
    tracesSampleRate: 0,
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 0,
    enableLogs: false,
    beforeSend: dropAbortedRequests
  });
  initialized2 = true;
}
function dropAbortedRequests(event, hint) {
  return isAbortError(hint.originalException) ? null : event;
}
function captureException2(error, context) {
  if (!initialized2) return;
  Sentry.captureException(error, {
    contexts: context?.componentStack ? { react: { componentStack: context.componentStack } } : void 0,
    extra: context?.extra
  });
}
function setUser2(user) {
  if (!initialized2) return;
  Sentry.setUser({ id: user.id, email: user.email ?? void 0 });
}
function clearUser() {
  if (!initialized2) return;
  Sentry.setUser(null);
}

// src/browser/web-vitals.ts
import { onCLS, onFCP, onINP, onLCP, onTTFB } from "web-vitals";
var started = false;
function startWebVitalsReporting(config = {}) {
  if (started) return;
  started = true;
  const endpoint2 = config.endpoint ?? "/v1/web-vitals";
  const send2 = (metric) => {
    const payload = {
      name: metric.name,
      value: metric.value,
      rating: metric.rating,
      id: metric.id,
      navigation_type: metric.navigationType,
      url_path: window.location.pathname
    };
    const body = JSON.stringify(payload);
    try {
      if (typeof navigator.sendBeacon === "function") {
        const blob = new Blob([body], { type: "application/json" });
        navigator.sendBeacon(endpoint2, blob);
        return;
      }
      void fetch(endpoint2, {
        method: "POST",
        body,
        headers: { "Content-Type": "application/json" },
        keepalive: true
      }).catch(() => {
      });
    } catch {
    }
  };
  onLCP(send2);
  onFCP(send2);
  onCLS(send2);
  onINP(send2);
  onTTFB(send2);
}
export {
  Sentry,
  captureException2 as captureException,
  clearUser,
  flush as flushAnalytics,
  getAnonymousId,
  identify,
  initAnalytics,
  initOtelBrowser,
  initSentryBrowser,
  isAbortError,
  log,
  reset,
  setUser2 as setUser,
  startWebVitalsReporting,
  track
};
